// Авиабилеты: состояние формы и поиска, адресная строка, история, умный поиск.
// Хранилища вне React: читаются синхронно (отправка формы сразу после записи значений внешним кодом).
import type { AssistantResult, FlightSearchResult, PlaceByCode, PriceAdvice, PriceInsight, Ticket, WhereAmI } from '@sayohat/shared';
import { cleanName } from '../../entities/place';
import { api } from '../../shared/lib/api';
import { bus } from '../../shared/lib/bus';
import { formatDateOrMonth, isoDate, formatMonth, today } from '../../shared/lib/format';
import { store } from '../../shared/lib/storage';
import { createStore, useStore } from '../../shared/lib/store';
import { track } from '../../shared/lib/track';
import type { Profile, SearchMode, SearchQuery } from '../../shared/lib/types';
import type { Picked } from '../../shared/ui/Autocomplete';
import { getProfile, partyOf } from '../profile';

// ================= Форма =================

export type InvalidField = 'origin' | 'destination' | 'departure' | 'ret' | 'retMonth' | null;

export interface FormState {
  origin: Picked | null;
  destination: Picked | null;
  departure: string;
  ret: string;
  depMonth: string;
  retMonth: string;
  mode: SearchMode;
  currency: string;
  direct: boolean;
  adults: number;
  children: number;
  infants: number;
  invalid: InvalidField;
}

/** Ближайшие 12 месяцев для режима «весь месяц» */
export function monthOptions(optional: boolean): { value: string; label: string }[] {
  const now = new Date();
  const list = optional ? [{ value: '', label: 'В одну сторону' }] : [];
  for (let i = 0; i < 12; i++) {
    const m = isoDate(new Date(now.getFullYear(), now.getMonth() + i, 1)).slice(0, 7);
    list.push({ value: m, label: formatMonth(m) });
  }
  return list;
}

/** Состав из профиля для авиабилетов: с 12 лет авиакомпании считают пассажира взрослым */
function flightParty(): { adults: number; children: number; infants: number } {
  const party = partyOf(getProfile());
  const teens = party.childAges.filter((a) => a >= 12).length;
  return { adults: party.adults + teens, children: party.children - teens, infants: party.infants };
}

/** Пассажиры прошлого поиска: в следующий раз форма открывается с ними (первый раз — состав из профиля) */
const PAX_KEY = 'pax';
const savedPax = (): { adults: number; children: number; infants: number } => {
  const p = store.get<{ adults?: number; children?: number; infants?: number } | null>(PAX_KEY, null) || flightParty();
  const n = (v: unknown, min: number, def: number) => (typeof v === 'number' && Number.isInteger(v) && v >= min && v <= 9 ? v : def);
  const adults = n(p?.adults, 1, 1), children = n(p?.children, 0, 0), infants = n(p?.infants, 0, 0);
  return adults + children > 9 || infants > adults ? { adults: 1, children: 0, infants: 0 } : { adults, children, infants };
};

export const formStore = createStore<FormState>({
  origin: null,
  destination: null,
  departure: '',
  ret: '',
  depMonth: today().slice(0, 7),
  retMonth: '',
  mode: 'exact',
  currency: store.get('currency', 'usd'),
  direct: false,
  ...savedPax(),
  invalid: null,
});
export const useFlightForm = () => useStore(formStore);

export function setCurrency(currency: string): void {
  store.set('currency', currency);
  formStore.set({ currency });
}

export function setMode(mode: SearchMode): void {
  const f = formStore.get();
  const patch: Partial<FormState> = { mode };
  if (mode === 'month') {
    if (f.departure) patch.depMonth = f.departure.slice(0, 7);
    patch.retMonth = f.ret ? f.ret.slice(0, 7) : '';
  }
  formStore.set(patch);
}

/** Даты из календаря формы: обратная не раньше вылета */
export function setDates(departure: string, ret: string): void {
  formStore.set({ departure, ret: ret && departure && ret < departure ? '' : ret, invalid: null });
}

export function setPax(next: { adults: number; children: number; infants: number }): boolean {
  if (next.adults < 1 || next.children < 0 || next.infants < 0 || next.adults + next.children > 9 || next.infants > next.adults) return false;
  formStore.set(next);
  return true;
}

export function swapPlaces(): void {
  const f = formStore.get();
  formStore.set({ origin: f.destination, destination: f.origin, invalid: null });
}

function fillForm(q: SearchQuery): void {
  const patch: Partial<FormState> = { mode: q.mode, currency: q.currency, direct: q.direct, adults: q.adults || 1, children: q.children || 0, infants: q.infants || 0 };
  if (q.mode === 'exact') {
    patch.departure = q.departure || '';
    patch.ret = q.ret || '';
  } else {
    patch.depMonth = q.departure;
    patch.retMonth = q.ret || '';
  }
  formStore.set(patch);
  if (q.currency) store.set('currency', q.currency);
}

const placeOf = (code: string, name?: string): Picked | null => (code ? { label: name ? `${name} (${code})` : code, value: code } : null);

// ================= Поиск =================

export interface SearchResultState {
  forId: number;
  tickets: Ticket[];
  nearby: Ticket[];
  currency: string;
  allUrl: string;
  insight: PriceInsight | null;
  /** «Купить сейчас или подождать» — по истории цен, которую копит сайт */
  advice: PriceAdvice | null;
}

export interface LandingState {
  /** номер запроса — по нему «куда угодно» перезагружается */
  id: number;
  origin: string;
  originName: string;
  currency: string;
  direct: boolean;
  month: string;
  /** города, подходящие под пожелание умного поиска */
  only: Set<string> | null;
  wish: string;
}

export interface AiChips {
  summary: string;
  maxPrice: number | null;
  nights: number | null;
  currency: string;
}

export interface FlightsState {
  query: SearchQuery | null;
  searchId: number;
  /** поиск по дню из календаря — календарь не перезагружаем */
  keepCalendar: boolean;
  error: string;
  searching: boolean;
  result: SearchResultState | null;
  landing: LandingState | null;
  ai: AiChips | null;
  budget: string;
  aiBusy: boolean;
}

export const flightsStore = createStore<FlightsState>({
  query: null, searchId: 0, keepCalendar: false, error: '', searching: false, result: null, landing: null, ai: null, budget: '', aiBusy: false,
});
export const useFlights = () => useStore(flightsStore);

/** Текущий запрос поиска авиабилетов или null (раздел отелей берёт из него даты) */
export const currentQuery = (): SearchQuery | null => flightsStore.get().query;

export const showError = (error: string): void => flightsStore.set({ error });

function queryToParams(q: SearchQuery): URLSearchParams {
  const p = new URLSearchParams({ from: q.origin });
  if (q.destination) p.set('to', q.destination);
  if (q.departure) p.set('depart', q.departure);
  if (q.ret) p.set('ret', q.ret);
  if (q.mode === 'month') p.set('mode', 'month');
  p.set('cur', q.currency);
  if (q.direct) p.set('direct', '1');
  if (q.adults > 1) p.set('adults', String(q.adults));
  if (q.children) p.set('children', String(q.children));
  if (q.infants) p.set('infants', String(q.infants));
  return p;
}

export function searchParams(q: SearchQuery): URLSearchParams {
  const p = new URLSearchParams({
    origin: q.origin, destination: q.destination, departure: q.departure, currency: q.currency, direct: String(q.direct),
    adults: String(q.adults), children: String(q.children), infants: String(q.infants),
  });
  if (q.ret) p.set('ret', q.ret);
  return p;
}

export function routeTitle(q: SearchQuery | null): string {
  if (!q) return '';
  return `${q.originName || q.origin} — ${q.destinationName || q.destination}, ${formatDateOrMonth(q.departure)}${q.ret ? ` — ${formatDateOrMonth(q.ret)}` : ''}`;
}

export async function runSearch(query: SearchQuery, { push = true, keepCalendar = false }: { push?: boolean; keepCalendar?: boolean } = {}): Promise<void> {
  const searchId = flightsStore.get().searchId + 1;
  const originLabel = formStore.get().origin?.label || query.originName || query.origin;
  store.set('origin', { label: originLabel, code: query.origin });
  store.set(PAX_KEY, { adults: query.adults || 1, children: query.children || 0, infants: query.infants || 0 });
  if (push) {
    const url = `/?${queryToParams(query)}#flights`;
    if (url !== location.pathname + location.search + location.hash) history.pushState(null, '', url);
  }
  if (!keepCalendar) track('search_flight', { origin: query.origin, destination: query.destination, label: `${query.originName || query.origin} — ${query.destinationName || 'куда угодно'}` });

  if (!query.destination) {
    flightsStore.set({ query, searchId, keepCalendar, error: '', result: null, searching: false });
    loadLanding(query.departure ? query.departure.slice(0, 7) : '');
    return;
  }

  saveHistory(query);
  flightsStore.set({ query, searchId, keepCalendar, error: '', result: null, searching: true });
  try {
    const data = await api<FlightSearchResult>(`/api/search?${searchParams(query)}`);
    if (flightsStore.get().searchId !== searchId) return;
    flightsStore.set({
      searching: false,
      result: { forId: searchId, tickets: data.tickets, nearby: data.nearby || [], currency: data.currency, allUrl: data.allUrl, insight: data.insight || null, advice: data.advice || null },
    });
    bus.emit('search:done', query);
    if (!data.tickets.length) track('no_results', { kind: 'flight', origin: query.origin, destination: query.destination, label: `${query.originName || query.origin} — ${query.destinationName || query.destination}` });
  } catch (err) {
    if (flightsStore.get().searchId !== searchId) return;
    flightsStore.set({ searching: false, error: err instanceof Error ? err.message : String(err) });
  }
}

/** Проверка формы и запрос (null — ошибка показана) */
export function readForm(): SearchQuery | null {
  const f = formStore.get();
  flightsStore.set({ error: '' });
  const fail = (field: InvalidField, msg: string) => {
    formStore.set({ invalid: field });
    showError(msg);
    return null;
  };
  const origin = f.origin?.value || '';
  const destination = f.destination?.value || '';
  if (!origin) return fail('origin', 'Выберите город вылета из списка подсказок');
  if ((f.destination?.label || '').trim() && !destination) return fail('destination', 'Выберите город прилёта из подсказок или оставьте поле пустым — покажем, куда дешевле');
  if (origin === destination) return fail('destination', 'Города вылета и прилёта совпадают');

  let departure: string, ret: string;
  const now = today();
  if (f.mode === 'exact') {
    departure = f.departure;
    ret = f.ret;
    if (!departure && destination) return fail('departure', 'Укажите дату вылета');
    if (departure && departure < now) return fail('departure', 'Дата вылета уже прошла');
    if (ret && !departure) return fail('departure', 'Укажите дату вылета');
    if (ret && ret < departure) return fail('ret', 'Дата возвращения раньше даты вылета');
  } else {
    departure = f.depMonth;
    ret = f.retMonth;
    if (ret && ret < departure) return fail('retMonth', 'Месяц возвращения раньше месяца вылета');
  }
  formStore.set({ invalid: null });
  return {
    origin, destination, departure, ret: destination ? ret : '', mode: f.mode,
    originName: cleanName(f.origin?.label || ''), destinationName: cleanName(f.destination?.label || ''),
    currency: f.currency, direct: f.direct, adults: f.adults, children: f.children, infants: f.infants,
  };
}

export function submitForm(): void {
  landingWish = { only: null, wish: '' };
  flightsStore.set({ ai: null });
  const q = readForm();
  if (q) void runSearch(q);
}

// ================= «Куда угодно» =================

let landingWish: { only: Set<string> | null; wish: string } = { only: null, wish: '' };

export function loadLanding(month = ''): void {
  const f = formStore.get();
  const origin = f.origin?.value || '';
  if (!origin) { flightsStore.set({ landing: null }); return; }
  const prev = flightsStore.get().landing;
  flightsStore.set({
    landing: {
      id: (prev?.id || 0) + 1, origin, originName: cleanName(f.origin?.label || ''), currency: f.currency, direct: f.direct,
      month, only: landingWish.only, wish: landingWish.wish,
    },
  });
}

/** Направление из «куда угодно»: поиск на выбранные даты или на дату самого дешёвого билета */
export function goTo(code: string, name: string, date: string): void {
  const f = formStore.get();
  formStore.set({ destination: placeOf(code, name) });
  let departure = f.mode === 'exact' ? f.departure : f.depMonth;
  let ret = f.mode === 'exact' ? f.ret : f.retMonth;
  if (!departure || (f.mode === 'exact' && departure < today())) { departure = date; ret = ''; }
  const q: SearchQuery = {
    origin: f.origin?.value || '', destination: code, originName: cleanName(f.origin?.label || ''), destinationName: name,
    departure, ret, mode: departure.length === 7 ? 'month' : 'exact', currency: f.currency, direct: f.direct,
    adults: f.adults, children: f.children, infants: f.infants,
  };
  fillForm(q);
  void runSearch(q);
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

/** Повторить поиск с изменённым запросом (день календаря, месяц, история) */
export function searchWith(q: SearchQuery, opts: { keepCalendar?: boolean } = {}): void {
  fillForm(q);
  void runSearch(q, opts);
}

// ================= История =================

export const historyStore = createStore({ items: store.get<SearchQuery[]>('history', []) });

function saveHistory(q: SearchQuery): void {
  const item: SearchQuery = {
    origin: q.origin, destination: q.destination, originName: q.originName, destinationName: q.destinationName,
    departure: q.departure, ret: q.ret, mode: q.mode, currency: q.currency, direct: q.direct,
    adults: q.adults, children: q.children, infants: q.infants,
  };
  const key = (x: SearchQuery) => [x.origin, x.destination, x.departure, x.ret].join('|');
  const items = [item, ...store.get<SearchQuery[]>('history', []).filter((x) => key(x) !== key(item))].slice(0, 6);
  store.set('history', items);
  historyStore.set({ items });
}

export function searchFromHistory(q: SearchQuery): void {
  formStore.set({ origin: placeOf(q.origin, q.originName), destination: placeOf(q.destination, q.destinationName) });
  searchWith(q);
}

// ================= Адресная строка =================

export async function restoreFromUrl(): Promise<boolean> {
  const p = new URLSearchParams(location.search);
  const from = p.get('from');
  if (!from) return false;
  const int = (k: string, d: number) => Number.parseInt(p.get(k) || '', 10) || d;
  const q: SearchQuery = {
    origin: from.toUpperCase(),
    destination: (p.get('to') || '').toUpperCase(),
    departure: p.get('depart') || '',
    ret: p.get('ret') || '',
    mode: p.get('mode') === 'month' ? 'month' : 'exact',
    currency: (p.get('cur') || store.get('currency', 'usd')).toLowerCase(),
    direct: p.get('direct') === '1',
    adults: int('adults', 1), children: int('children', 0), infants: int('infants', 0),
  };
  const [o, d] = await Promise.all([
    api<PlaceByCode>(`/api/place?code=${q.origin}`).catch(() => null),
    q.destination ? api<PlaceByCode>(`/api/place?code=${q.destination}`).catch(() => null) : null,
  ]);
  if (!o) return false;
  formStore.set({ origin: placeOf(o.code, o.name), destination: d ? placeOf(d.code, d.name) : null });
  q.originName = o.name;
  q.destinationName = d ? d.name : '';
  fillForm(q);
  if (q.destination && q.departure && q.departure.slice(0, 7) < today().slice(0, 7)) return false;
  void runSearch(q, { push: false });
  return true;
}

async function prefillOrigin(): Promise<void> {
  const saved = store.get<{ label: string; code: string } | null>('origin', null);
  if (saved) { formStore.set({ origin: { label: saved.label, value: saved.code } }); return; }
  // Город из профиля («Откуда я обычно еду») — точнее, чем по IP
  const home = getProfile().home;
  if (home) { formStore.set({ origin: placeOf(home.code, home.name) }); return; }
  try {
    const here = await api<WhereAmI | null>('/api/whereami');
    if (here && !formStore.get().origin?.value) formStore.set({ origin: placeOf(here.code, here.name) });
  } catch {
    // город не определился — пользователь выберет сам
  }
}

// ================= Умный поиск (Groq) =================

export async function askAi(text: string): Promise<void> {
  track('ai_search');
  flightsStore.set({ aiBusy: true, error: '' });
  try {
    const r = await api<AssistantResult>('/api/assistant', { method: 'POST', body: { text, origin: formStore.get().origin?.value } });
    applyAi(r);
  } catch (err) {
    showError(err instanceof Error ? err.message : String(err));
  } finally {
    flightsStore.set({ aiBusy: false });
  }
}

function applyAi(r: AssistantResult): void {
  const f0 = formStore.get();
  if (r.origin && r.origin.code) formStore.set({ origin: { label: r.origin.name ? `${r.origin.name} (${r.origin.code})` : f0.origin?.label || '', value: r.origin.code } });
  const f = formStore.get();
  if (!f.origin?.value) { showError('Укажите, откуда вылетаете, — в запросе или в поле «Откуда»'); return; }
  const currency = r.currency || f.currency;
  if (r.currency) setCurrency(r.currency);
  formStore.set({ adults: r.adults, children: r.children, infants: r.infants, direct: r.direct });

  const q: SearchQuery = {
    origin: f.origin.value, originName: cleanName(f.origin.label),
    destination: r.destination ? r.destination.code : '', destinationName: r.destination ? r.destination.name : '',
    departure: r.departure, ret: r.destination ? r.ret : '', mode: r.departure.length === 7 ? 'month' : 'exact',
    currency, direct: r.direct, adults: r.adults, children: r.children, infants: r.infants,
  };
  formStore.set({ destination: r.destination ? placeOf(r.destination.code, r.destination.name) : null });

  // Что понял помощник
  const chips: AiChips = { summary: r.summary || '', maxPrice: r.maxPrice || null, nights: r.nights || null, currency };
  flightsStore.set({ ai: chips.summary || chips.maxPrice || chips.nights ? chips : null, budget: r.maxPrice ? String(r.maxPrice) : '' });

  if (!q.destination) {
    // Без конкретного города — «куда угодно»; подходящие под пожелание города идут первыми
    landingWish = { only: r.candidates && r.candidates.length ? new Set(r.candidates.map((c) => c.code)) : null, wish: r.wish || '' };
    fillForm(q);
    void runSearch(q);
    return;
  }
  if (!q.departure) Object.assign(q, { mode: 'month', departure: today().slice(0, 7), ret: '' });
  fillForm(q);
  void runSearch(q);
}

// ================= Запуск =================

let started = false;

export async function initFlights(): Promise<void> {
  if (started) return;
  started = true;
  // Профиль: прямые рейсы по умолчанию (если поиск не из ссылки), смена валюты
  const p = getProfile();
  if (p.direct && !location.search.includes('from=')) formStore.set({ direct: true });
  let home = p.home?.code || '';
  bus.on('profile:changed', (pr: Profile) => {
    if (pr.currency && pr.currency !== store.get('currency', 'usd')) setCurrency(pr.currency);
    // Новый домашний город — сразу в «Откуда», если сейчас не показана выдача
    if (pr.home && pr.home.code !== home && !flightsStore.get().query) {
      formStore.set({ origin: placeOf(pr.home.code, pr.home.name), invalid: null });
      store.set('origin', { label: `${pr.home.name} (${pr.home.code})`, code: pr.home.code });
    }
    home = pr.home?.code || '';
    if (pr.direct && !formStore.get().direct && !location.search.includes('from=')) formStore.set({ direct: true });
  });
  window.addEventListener('popstate', () => { if (!location.pathname.startsWith('/p/')) void restoreFromUrl(); });

  if (!(await restoreFromUrl())) {
    await prefillOrigin();
    loadLanding();
  }
}

/** Выбран город вылета, а поиска ещё не было — показываем «куда угодно» из него */
export function onOriginPicked(): void {
  if (!flightsStore.get().query) loadLanding();
}

